import React, { useEffect, useState, useCallback } from "react";
import toast from "react-hot-toast";
import Backend from "../../../../../Backend/Backend";
import { useSelector } from "react-redux";
import useSound from "use-sound";
import ErrorSoundFile from "../../../../../Sound/Error.wav";

const fmt = require("indian-number-format");
const backend = await new Backend().init();

export default function TopCard({ user_id, userType, standing }) {
  const total = useSelector((state) => state.total);
  const admin_pf = useSelector((state) => state.admin_pf);

  const [playError] = useSound(ErrorSoundFile);

  const [loading, setLoading] = useState(false);
  const [credit, setCredit] = useState(0);
  const [depositCurrent, setDepositCurrent] = useState(0);
  const [profitLoss, setProfitLoss] = useState(0);
  const [equity, setEquity] = useState(0);
  const [margin, setMargin] = useState(0);
  const [marginFree, setMarginFree] = useState(0);

  const [isDollar, setisDollar] = useState(0);
  const [intradaySq, setIntradaySq] = useState(0);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState([]);

  const loadProfitLoss = useCallback(async () => {
    if (!userType) return;
    if (userType !== 4 && !user_id) return;

    setLoading(true);

    try {
      const baseData = {
        server: localStorage.getItem("server"),
        id: localStorage.getItem("id"),
        username: localStorage.getItem("username"),
        token: localStorage.getItem("token"),
      };

      const data =
        userType === 4 ? baseData : { ...baseData, user_id: user_id };

      let res;
      switch (userType) {
        case 1:
          res = await backend.profit_loss_admin(data);
          break;
        case 2:
          res = await backend.profit_loss_super(data);
          break;
        case 3:
          res = await backend.profit_loss_master(data);
          break;
        case 4:
          res = await backend.profit_loss(data);
          break;
        case 5:
          res = await backend.profit_loss_broker(data);
          break;
        default:
          return;
      }

      if (res?.error === "False") {
        setCredit(+res.credit || 0);
        setDepositCurrent(+res.deposit_current || 0);
        setProfitLoss(+res.profit_loss || 0);
        setEquity(+res.equity || 0);
        setMargin(+res.margin || 0);
        setMarginFree(+res.marginfree || 0);
        setisDollar(res.isDollar);
        setIntradaySq(res.intraday_sq);
      } else {
        if (res?.logout === 1) {
          window.LogOut?.();
        } else {
          playError();
          toast.error(res?.message || "Failed to load P/L data");
        }
      }
    } catch (err) {
      playError();
      toast.error("Error loading P/L data");
      console.error("TopCard Load Error:", err);
    } finally {
      setLoading(false);
    }
  }, [user_id, userType, playError]);

  useEffect(() => {
    if (userType) loadProfitLoss();
  }, [userType, loadProfitLoss]);

  const netEquity = (parseFloat(equity) + parseFloat(total || 0)).toFixed(2);
  const marginPercent =
    netEquity > 0 ? Math.round((marginFree / netEquity) * 100) : 0;
  const netAdminPf = admin_pf < 0 ? -admin_pf : admin_pf;
  const showUserData = user_id && user_id !== 0;

  const totalBuyQty = standing
    .filter((x) => x.buy_sell === 0)
    .reduce((sum, x) => sum + Number(x.qty || 0), 0);

  const totalSellQty = standing
    .filter((x) => x.buy_sell === 1)
    .reduce((sum, x) => sum + Number(x.qty || 0), 0);

  const totalBuyCount = standing.filter((x) => x.buy_sell === 0).length;

  // ⭐ NEW — Total SELL Count
  const totalSellCount = standing.filter((x) => x.buy_sell === 1).length;

  const groupByExchange = () => {
    const map = {};

    standing.forEach((item) => {
      const exch = item.type || "UNKNOWN";

      if (!map[exch]) {
        map[exch] = {
          buyQty: 0,
          buyVolume: 0,
          sellQty: 0,
          sellVolume: 0,
          buyCount: 0,
          sellCount: 0,
        };
      }

      if (item.buy_sell === 0) {
        map[exch].buyQty += Number(item.qty || 0);
        map[exch].buyVolume += Number(item.limit_block || 0);
        map[exch].buyCount += 1;
      }

      if (item.buy_sell === 1) {
        map[exch].sellQty += Number(item.qty || 0);
        map[exch].sellVolume += Number(item.limit_block || 0);
        map[exch].sellCount += 1;
      }
    });

    return Object.entries(map).map(([exch, data]) => ({
      exchange: exch,
      ...data,
    }));
  };

  const getMostStandingSymbol = useCallback(() => {
    if (!standing || standing.length === 0) return null;

    const map = {};

    standing.forEach((item) => {
      const sym = item.symbol || "UNKNOWN";

      if (!map[sym]) map[sym] = 0;

      map[sym] += Number(item.qty || 0);
    });

    // Find symbol with max quantity
    let maxSymbol = null;
    let maxQty = 0;

    for (const sym in map) {
      if (map[sym] > maxQty) {
        maxSymbol = sym;
        maxQty = map[sym];
      }
    }

    return { symbol: maxSymbol, qty: maxQty };
  }, [standing]);

  const cardStyle = {
    minWidth: "120px",
    padding: "6px 12px",
    borderRadius: "8px",
    background: "var(--background)",
    border: "2px solid var(--hover)",
    color: "var(--text)",
    display: "flex",
    flexDirection: "column",
  };

  const labelStyle = {
    fontSize: "0.7rem",
    opacity: 0.7,
    fontWeight: 500,
  };

  const valueStyle = {
    fontSize: "1rem",
    fontWeight: 700,
  };

  return (
    <div
      style={{
        display: "flex",
        gap: "16px",
        flexWrap: "wrap",
        padding: "10px 20px",
        background: "var(--background)",
        color: "var(--text)",
        borderBottom: "1px solid var(--hover)",
        alignItems: "center",
      }}
    >
      {/* LEFT SIDE CARDS */}
      <div style={cardStyle}>
        <span style={labelStyle}>TOTAL P/L</span>
        <span
          style={{
            ...valueStyle,
            color: total >= 0 ? "var(--buymeta)" : "var(--sellmeta)",
          }}
        >
          {fmt.format(total.toFixed(2))} {isDollar === 1 ? "$" : ""}
        </span>
      </div>

      {(showUserData || userType === 4) && (
        <>
          <div style={cardStyle}>
            <span style={labelStyle}>BOOKED</span>
            <span
              style={{
                ...valueStyle,
                color: profitLoss >= 0 ? "var(--buymeta)" : "var(--sellmeta)",
              }}
            >
              {fmt.format(profitLoss.toFixed(2))} {isDollar === 1 ? "$" : ""}
            </span>
          </div>

          <div style={cardStyle}>
            <span style={labelStyle}>CREDIT</span>
            <span style={valueStyle}>{fmt.format(credit.toFixed(2))} {isDollar === 1 ? "$" : ""}</span>
          </div>

          <div style={cardStyle}>
            <span style={labelStyle}>EQUITY</span>
            <span style={valueStyle}>{fmt.format(netEquity)} {isDollar === 1 ? "$" : ""}</span>
          </div>

          <div style={cardStyle}>
            <span style={labelStyle}>MARGIN</span>
            <span style={valueStyle}>{fmt.format(margin.toFixed(2))}</span>
          </div>

          <div style={cardStyle}>
            <span style={labelStyle}>FREE MARGIN</span>
            <span style={valueStyle}>{fmt.format(marginFree.toFixed(2))}</span>
          </div>

          <div style={cardStyle}>
            <span style={labelStyle}>MARGIN LEVEL (%)</span>
            <span style={valueStyle}>{marginPercent}%</span>
          </div>
        </>
      )}

      {/** ADMIN SELF P/L */}
      {(userType === 1 || userType === 2 || userType === 3 || userType === 5) && (
        <div style={cardStyle}>
          <span style={labelStyle}>SELF PROFIT LOSS</span>
          <span
            style={{
              ...valueStyle,
              color: admin_pf >= 0 ? "var(--sell)" : "var(--buy)",
            }}
          >
            {fmt.format(netAdminPf.toFixed(0))} {isDollar === 1 ? "$" : ""}
          </span>
        </div>
      )}

      {/** ⭐⭐⭐ RIGHT SIDE — NET BUY & NET SELL ⭐⭐⭐ */}

      {userType === 4 || showUserData ? null : (
        <div style={{ marginLeft: "auto", display: "flex", gap: "16px" }}>
          <div
            onClick={() => {
              setModalData(groupByExchange());
              setModalOpen(true);
            }}
            style={cardStyle}
          >
            <span style={labelStyle}>NET BUY : {totalBuyCount}</span>
            <span
              style={{
                ...valueStyle,
                color: "var(--buy)",
              }}
            >
              {fmt.format(totalBuyQty)}
            </span>
          </div>

          <div
            onClick={() => {
              setModalData(groupByExchange());
              setModalOpen(true);
            }}
            style={cardStyle}
          >
            <span style={labelStyle}>NET QTY (B-S)</span>
            <span
              style={{
                ...valueStyle,
                color: "var(--primary)",
              }}
            >
              {fmt.format(totalBuyQty - totalSellQty)}
            </span>
          </div>

          <div
            onClick={() => {
              setModalData(groupByExchange());
              setModalOpen(true);
            }}
            style={cardStyle}
          >
            <span style={labelStyle}>NET SELL : {totalSellCount}</span>
            <span
              style={{
                ...valueStyle,
                color: "var(--sell)",
              }}
            >
              {fmt.format(totalSellQty)}
            </span>
          </div>

          <div style={cardStyle}>
            <span style={labelStyle}>TOP SYMBOL</span>
            <span style={valueStyle}>
              {getMostStandingSymbol()?.symbol || "-"} (
              {getMostStandingSymbol()?.qty || 0})
            </span>
          </div>
        </div>
      )}

      {modalOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(0,0,0,0.35)",
            backdropFilter: "blur(4px)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
            padding: "20px",
          }}
          onClick={() => setModalOpen(false)}
        >
          <div
            style={{
              background: "var(--background)",
              padding: "24px",
              borderRadius: "14px",
              minWidth: "750px",
              width: "100%",
              maxWidth: "850px",
              color: "var(--text)",
              transform: "scale(1)",
              animation: "popIn 0.15s ease-out",
              border: "2px solid var(--hover)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Title + Close button */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "15px",
              }}
            >
              <h3 style={{ fontSize: "1.25rem", fontWeight: 600 }}>
                Exchange-Wise Quantity/Volume
              </h3>
              <button
                onClick={() => setModalOpen(false)}
                style={{
                  background: "transparent",
                  border: "none",
                  fontSize: "1.4rem",
                  color: "var(--text)",
                  cursor: "pointer",
                  padding: 0,
                }}
              >
                ×
              </button>
            </div>

            {/* Table */}
            <div
              style={{
                borderRadius: "10px",
                overflow: "hidden",
                border: "1px solid var(--hover)",
              }}
            >
              <table
                style={{
                  width: "100%",
                  fontSize: "0.95rem",
                  borderCollapse: "collapse",
                }}
              >
                <thead>
                  <tr
                    style={{
                      background: "var(--background)",
                      textAlign: "left",
                      fontWeight: 600,
                    }}
                  >
                    <th style={{ padding: "10px" }}>Exchange</th>
                    <th style={{ padding: "10px" }}>Buy Qty</th>
                    <th style={{ padding: "10px" }}>Buy Volume</th>
                    <th style={{ padding: "10px" }}>Sell Qty</th>
                    <th style={{ padding: "10px" }}>Sell Volume</th>
                    <th style={{ padding: "10px" }}>Buy Count</th>
                    <th style={{ padding: "10px" }}>Sell Count</th>
                  </tr>
                </thead>

                <tbody>
                  {modalData.map((x) => (
                    <tr
                      key={x.exchange}
                      style={{
                        background: "var(--background)",
                        borderBottom: "2px solid var(--hover)",
                        fontSize: "16px",
                      }}
                    >
                      <td style={{ padding: "10px 12px" }}>
                        {x.exchange === "nse"
                          ? "NSE-FUTURE"
                          : x.exchange === "fo"
                          ? "NSE-OPTIONS"
                          : x.exchange === "mcx"
                          ? "MCX-FUTURE"
                          : x.exchange === "comex_future"
                          ? "COMEX"
                          : x.exchange === "crypto_future"
                          ? "CRYPTO"
                          : x.exchange === "forex_future"
                          ? "FOREX"
                          : x.exchange === "us_stock"
                          ? "US-STOCK"
                          : x.exchange === "sgx"
                          ? "SGX"
                          : x.exchange === "index"
                          ? "OTHERS"
                          : x.exchange}
                      </td>
                      <td style={{ padding: "10px 12px", color: "var(--buy)" }}>
                        {x.buyQty}
                      </td>
                      <td style={{ padding: "10px 12px", color: "var(--buy)" }}>
                        {x.buyVolume}
                      </td>
                      <td
                        style={{ padding: "10px 12px", color: "var(--sell)" }}
                      >
                        {x.sellQty}
                      </td>
                      <td
                        style={{ padding: "10px 12px", color: "var(--sell)" }}
                      >
                        {x.sellVolume}
                      </td>
                      <td style={{ padding: "10px 12px", color: "var(--buy)" }}>
                        {x.buyCount}
                      </td>
                      <td
                        style={{ padding: "10px 12px", color: "var(--sell)" }}
                      >
                        {x.sellCount}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
